import { useEffect } from "react";
import { addEventListenerAllWindows } from "~/utils/popoutWindowsRegistry";

/**
 * Global keyboard shortcuts hook
 * Handles keyboard events with proper input exclusion and cross-platform modifier keys
 *
 * @param {Object} shortcuts - Map of key combinations to handler functions
 * Example: { '1': () => {}, 'Ctrl+I': () => {}, 'Ctrl+D': () => {} }
 */
export const useKeyboardShortcuts = (shortcuts = {}) => {
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Ignore keyboard shortcuts when typing in inputs, textareas, or contenteditable elements
      const target = event.target;
      const isInput =
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable;

      if (isInput) return;

      // Detect modifier keys (Cmd on Mac, Ctrl on Windows/Linux)
      const isMod = event.metaKey || event.ctrlKey;
      const isShift = event.shiftKey;
      const isAlt = event.altKey;

      // Build shortcut key string
      let shortcutKey = "";
      if (isMod) shortcutKey += "Ctrl+";
      if (isShift) shortcutKey += "Shift+";
      if (isAlt) shortcutKey += "Alt+";

      // For letter keys, use uppercase; for special keys keep as-is
      const key = event.key.length === 1 ? event.key.toUpperCase() : event.key;
      shortcutKey += key;

      // Debug logging (remove in production)
      if (import.meta.env.DEV && (isMod || event.key === "F1")) {
        console.log("Keyboard shortcut detected:", {
          key: event.key,
          code: event.code,
          shortcutKey,
          metaKey: event.metaKey,
          ctrlKey: event.ctrlKey,
          shiftKey: event.shiftKey,
        });
      }

      // Check if we have a handler for this shortcut
      const handler = shortcuts[shortcutKey];

      if (handler) {
        event.preventDefault();
        event.stopPropagation();
        handler(event);
      }
    };

    // Add global keyboard listener — every window (PoC #popout)
    const detach = addEventListenerAllWindows("keydown", handleKeyDown);

    // Cleanup on unmount
    return detach;
  }, [shortcuts]);
};

export default useKeyboardShortcuts;
